import React from 'react';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useSettingsContext } from '../context/SettingsContext';
import { GamifyIcon } from '../../../components/GamifyIcon';
import { usePermissions } from '../../../context/PermissionsContext/PermissionsContext';
import featureFlag from '../../../common/featureFlag';
import { isStarterAccount } from '../../../common/utils/isStarterAccount';

const drawerWidth = 285;

const settingsNav = [
  {
    label: 'General Information',
    value: 'general',
    icon: 'globe',
    disabled: false,
  },
  {
    label: 'Customer-Facing Profile',
    value: 'customer-profile',
    icon: 'userCircle',
    disabled: false,
  },
  {
    label: 'Company Information',
    value: 'company',
    icon: 'about',
    disabled: false,
  },

  {
    label: 'Sales Tools Configuration',
    value: 'config',
    icon: 'config',
    disabled: false,
  },
  // {
  //   label: 'Integrations',
  //   value: 'integrations',
  //   icon: 'link',
  //   disabled: false,
  // },

  // {
  //   label: 'Hierarchy',
  //   value: 'hierarchy',
  //   icon: 'teams',
  //   disabled: true,
  // },
  {
    label: 'AI Settings',
    value: 'ai',
    icon: 'robot',
    disabled: false, // Will be dynamically set based on super admin status
  },
  {
    label: 'DocuSeal Visibility',
    value: 'docuseal',
    icon: 'notes',
    disabled: false,
  },
];

const SettingsSideBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setSelectedSetting, hasUnsavedChanges } = useSettingsContext();
  const { isSuperAdmin } = usePermissions();
  const isStarter = isStarterAccount();

  const handleNavigation = (value: string) => {
    console.log('Navigating to', value);
    if (pathname.includes('roles') && hasUnsavedChanges) {
      const confirmNavigation = window.confirm(
        'You have unsaved changes. Do you want to discard them and navigate to the new page?'
      );
      if (confirmNavigation) {
        setSelectedSetting(value as any); // Update the context
        navigate(`/settings/${value}`); // Navigate to the correct settings page
      } else return;
    }
    // console.log(value);
    setSelectedSetting(value as any); // Update the context
    navigate(`/settings/${value}`); // Navigate to the correct settings page
  };

  return (
    <Drawer
      sx={(theme) => ({
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          marginTop: 11,
          marginLeft: 0,
          borderRadius: 0,
          backgroundColor: theme.custom?.background1,
          borderRight: `1px solid ${theme.custom?.stroke}`,
        },
      })}
      variant="persistent"
      anchor="left"
      open={true}
    >
      <Divider />
      <List sx={{ mt: 3, pr: 5, pl: 5 }}>
        <Typography
          variant="h6"
          sx={(theme) => ({
            pt: 5,
            pb: 5,
            fontWeight: 780,
            color: theme.custom?.text,
          })}
        >
          Settings
        </Typography>
        <Typography
          variant="body1"
          sx={(theme) => ({
            pt: 0,
            pb: 2,
            fontWeight: 400,
            color: theme.custom?.textLight,
          })}
        >
          Account Setup
        </Typography>
        {settingsNav
          .filter((setting) => {
            // Filter out AI Settings if user doesn't have permission
            if (setting.value === 'ai') {
              return isSuperAdmin() && featureFlag('managePrompts');
            }
            return true;
          })
          .map((setting) => {
            const allowedForStarter = [
              'general',
              'company',
              'customer-profile',
            ];
            const isDisabled = isStarter
              ? !allowedForStarter.includes(setting.value)
              : setting.disabled;

            return (
              <ListItem key={setting.value} disablePadding>
                <ListItemButton
                  disabled={isDisabled}
                  selected={pathname.includes(setting.value)}
                  sx={(theme) => ({
                    pl: 0,
                    pr: 0,
                    color: theme.custom?.text,
                  })}
                  onClick={() => handleNavigation(setting.value)}
                >
                  <GamifyIcon icon={setting.icon} color="inherit" />
                  <ListItemText
                    primaryTypographyProps={{
                      marginLeft: 1,
                      fontWeight: pathname.includes(setting.value)
                        ? 500
                        : 'normal',
                    }}
                    primary={setting.label}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>
    </Drawer>
  );
};

export { SettingsSideBar };
